import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useMemo, useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ICONS,
  Icon,
  Input,
  cn,
  useToast,
  type IconName,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { AyarlarSekmeleri } from './ayarlar-sekmeler'

type CatalogEntry = components['schemas']['CatalogEntry']
type NavOverride = components['schemas']['NavOverride']

/** Düzenleyicideki bir satır: katalog değeri + kullanıcının değiştirdikleri. */
type Satir = {
  key: string
  varsayilan: CatalogEntry
  label: string
  icon: string
  group: string
  visible: boolean
}

const IKON_ADLARI = Object.keys(ICONS) as IconName[]

function satirlariKur(katalog: CatalogEntry[], override: NavOverride[]): Satir[] {
  const fark = new Map(override.map((item) => [item.key, item]))
  // Sıra AYRI bir haritada: satır nesnesine geçici bir alan koyup sonra
  // ayıklamak, o alanın kaçak kalmasına açık kapı bırakırdı.
  const sira = new Map(
    katalog.map((entry) => [entry.key, fark.get(entry.key)?.order ?? entry.order]),
  )

  return [...katalog]
    .map((entry) => {
      const o = fark.get(entry.key)
      return {
        key: entry.key,
        varsayilan: entry,
        label: o?.label ?? entry.label,
        icon: o?.icon ?? entry.icon,
        group: o?.group ?? entry.group ?? '',
        visible: o?.visible ?? true,
      }
    })
    .sort((a, b) => (sira.get(a.key) ?? 0) - (sira.get(b.key) ?? 0) || a.key.localeCompare(b.key))
}

/**
 * Kaydedilecek farkı üretir.
 *
 * Yalnızca DEĞİŞENLER gönderiliyor: katalogla aynı olan bir öğe için override
 * yazmak, YES-122'nin "yeni modül kendiliğinden görünsün" özelliğini bozardı —
 * her öğe için satır tutulursa sonradan eklenen modül, kaydedilmiş sıralamanın
 * neresine gireceği belirsiz kalırdı.
 *
 * Sıra bir İSTİSNA: göreli sıralama ancak bütün olarak anlamlı. Kullanıcı
 * sırayı hiç değiştirmediyse `order` hiç yazılmıyor; değiştirdiyse hepsine
 * yazılıyor.
 */
export function farkUret(satirlar: Satir[]): NavOverride[] {
  const varsayilanSira = [...satirlar]
    .sort((a, b) => a.varsayilan.order - b.varsayilan.order || a.key.localeCompare(b.key))
    .map((satir) => satir.key)
  const siraDegisti = satirlar.some((satir, index) => satir.key !== varsayilanSira[index])

  const fark: NavOverride[] = []
  satirlar.forEach((satir, index) => {
    const v = satir.varsayilan
    const item: NavOverride = { key: satir.key, visible: satir.visible }
    let degisti = !satir.visible

    if (satir.label !== v.label) {
      item.label = satir.label
      degisti = true
    }
    if (satir.icon !== v.icon) {
      item.icon = satir.icon
      degisti = true
    }
    if (satir.group !== (v.group ?? '')) {
      item.group = satir.group || null
      degisti = true
    }
    if (siraDegisti) {
      item.order = (index + 1) * 10
      degisti = true
    }

    if (degisti) fark.push(item)
  })
  return fark
}

/**
 * Ayarlar › Menü — YES-122.
 *
 * Veri getiren kabuk ile düzenleyici AYRI: düzenleyici taslağı `useState`
 * başlangıcında kuruyor ve sunucu verisi değiştiğinde `key` ile yeniden
 * monte ediliyor. Önceki hâli veriyi bir `useEffect` içinde state'e
 * kopyalıyordu; bu, her sunucu yanıtında ikinci bir render tetikliyor ve
 * kullanıcının yazdığını sessizce ezme riski taşıyordu.
 */
export function AyarlarMenu() {
  const slug = useTenantSlug()
  const [rolKapsami, setRolKapsami] = useState('')

  const katalog = useQuery({
    queryKey: ['nav-catalog', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/navigation/catalog')
      return data ?? []
    },
  })

  const roller = useQuery({
    queryKey: ['roles', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/roles')
      return data ?? []
    },
  })

  const yapilandirma = useQuery({
    queryKey: ['nav-config', slug, rolKapsami],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/navigation/config', {
        params: { query: { role_scope: rolKapsami || undefined } },
      })
      return data
    },
  })

  const kapsamSecici = (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium">Kimin için</span>
      <select
        className="h-10 min-w-56 rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
        value={rolKapsami}
        onChange={(event) => setRolKapsami(event.target.value)}
      >
        <option value="">Tüm yöneticiler</option>
        {(roller.data ?? []).map((rol) => (
          <option key={rol.code} value={rol.code}>
            {rol.name}
          </option>
        ))}
      </select>
    </label>
  )

  if (katalog.isError || yapilandirma.isError) {
    const hata = katalog.error ?? yapilandirma.error
    const yetkisiz = hata instanceof ApiError && hata.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm">
            {yetkisiz
              ? 'Menüyü düzenlemek için ayrı bir yetki gerekiyor.'
              : 'Menü yapılandırması yüklenemedi.'}
          </CardBody>
        </Card>
      </Sayfa>
    )
  }

  if (!katalog.data || !yapilandirma.data) {
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm text-[var(--ad-text-muted)]">Yükleniyor…</CardBody>
        </Card>
      </Sayfa>
    )
  }

  return (
    <Sayfa>
      <Duzenleyici
        // Sunucu verisi yenilenince taslak sıfırdan kurulsun.
        key={`${rolKapsami}:${yapilandirma.dataUpdatedAt}`}
        slug={slug}
        rolKapsami={rolKapsami}
        kapsamSecici={kapsamSecici}
        katalog={katalog.data}
        kayitliOverride={yapilandirma.data.items ?? []}
      />
    </Sayfa>
  )
}

function Duzenleyici({
  slug,
  rolKapsami,
  kapsamSecici,
  katalog,
  kayitliOverride,
}: {
  slug: string | null
  rolKapsami: string
  kapsamSecici: React.ReactNode
  katalog: CatalogEntry[]
  kayitliOverride: NavOverride[]
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const kayitli = useMemo(() => satirlariKur(katalog, kayitliOverride), [katalog, kayitliOverride])
  const [satirlar, setSatirlar] = useState<Satir[]>(() => kayitli)
  const [suruklenen, setSuruklenen] = useState<string | null>(null)

  const degisiklikSayisi = useMemo(
    () =>
      satirlar.filter((satir, index) => {
        const onceki = kayitli.find((item) => item.key === satir.key)
        if (!onceki) return false
        return (
          onceki.label !== satir.label ||
          onceki.icon !== satir.icon ||
          onceki.group !== satir.group ||
          onceki.visible !== satir.visible ||
          kayitli[index]?.key !== satir.key
        )
      }).length,
    [satirlar, kayitli],
  )

  const tazele = () => {
    void queryClient.invalidateQueries({ queryKey: ['nav-config', slug] })
    // Sol menü de bu yapılandırmadan besleniyor; anında güncellensin.
    void queryClient.invalidateQueries({ queryKey: ['navigation', slug] })
  }

  const kaydet = useMutation({
    mutationFn: async () => {
      await api.PUT('/api/v1/navigation/config', {
        params: { query: { role_scope: rolKapsami || undefined } },
        body: { items: farkUret(satirlar), group_labels: {} },
      })
    },
    onSuccess: () => {
      tazele()
      toast({ title: 'Menü kaydedildi', tone: 'success' })
    },
    onError: (error) =>
      toast({
        title: 'Kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const sifirla = useMutation({
    mutationFn: async () => {
      await api.DELETE('/api/v1/navigation/config', {
        params: { query: { role_scope: rolKapsami || undefined } },
      })
    },
    onSuccess: () => {
      tazele()
      toast({ title: 'Varsayılana dönüldü', tone: 'success' })
    },
    onError: () => toast({ title: 'Sıfırlanamadı', tone: 'error' }),
  })

  const guncelle = (key: string, degisim: Partial<Satir>) =>
    setSatirlar((onceki) =>
      onceki.map((satir) => (satir.key === key ? { ...satir, ...degisim } : satir)),
    )

  const tasi = (index: number, yon: -1 | 1) =>
    setSatirlar((onceki) => {
      const liste = [...onceki]
      const hedef = index + yon
      if (hedef < 0 || hedef >= liste.length) return liste
      ;[liste[index], liste[hedef]] = [liste[hedef], liste[index]]
      return liste
    })

  const birak = (hedefKey: string) =>
    setSatirlar((onceki) => {
      const liste = [...onceki]
      const kaynak = liste.findIndex((satir) => satir.key === suruklenen)
      const hedef = liste.findIndex((satir) => satir.key === hedefKey)
      if (kaynak < 0 || hedef < 0 || kaynak === hedef) return liste
      const [tasinan] = liste.splice(kaynak, 1)
      liste.splice(hedef, 0, tasinan)
      return liste
    })

  const gruplar = [...new Set(satirlar.map((satir) => satir.group).filter(Boolean))]

  // Sıralama grupları böler mi? Model düz bir liste + öğe başına grup olduğu
  // için, bir grubun öğeleri araya başka grup girecek şekilde dağıtılırsa
  // başlık iki kez yazılır. Bunu ENGELLEMEK yerine SÖYLÜYORUZ: taşımayı
  // kısıtlamak ya da öğenin grubunu kendiliğinden değiştirmek, kullanıcının
  // istemediği bir kararı sessizce vermek olurdu.
  const bolunmusGruplar = gruplar.filter((grup) => {
    const gorunen = satirlar.filter((satir) => satir.visible)
    let blok = 0
    gorunen.forEach((satir, index) => {
      if (satir.group === grup && gorunen[index - 1]?.group !== grup) blok += 1
    })
    return blok > 1
  })

  return (
    <>
      <Card>
        <CardBody className="flex flex-wrap items-end gap-3">
          {kapsamSecici}

          <div className="ml-auto flex items-center gap-2">
            {bolunmusGruplar.length > 0 && (
              <Badge tone="warning">
                {bolunmusGruplar.join(', ')} grubu bölündü — başlık iki kez görünecek
              </Badge>
            )}
            {degisiklikSayisi > 0 && (
              <Badge tone="warning">{degisiklikSayisi} kaydedilmemiş değişiklik</Badge>
            )}
            <Button
              variant="secondary"
              disabled={sifirla.isPending}
              onClick={() => sifirla.mutate()}
            >
              Varsayılana dön
            </Button>
            <Button
              disabled={kaydet.isPending || degisiklikSayisi === 0}
              onClick={() => kaydet.mutate()}
            >
              {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <Card>
          <CardHeader
            title="Öğeler"
            description="Gizlemek menüden kaldırır; YETKİ VERMEZ ya da ALMAZ. Gizli bir ekrana adresi bilen yetkili kullanıcı yine girebilir."
          />
          <CardBody className="flex flex-col gap-2">
            {satirlar.map((satir, index) => (
              <div
                key={satir.key}
                draggable
                onDragStart={() => setSuruklenen(satir.key)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => {
                  birak(satir.key)
                  setSuruklenen(null)
                }}
                className={cn(
                  'flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] border px-3 py-2',
                  satir.visible
                    ? 'border-[var(--ad-border)]'
                    : 'border-dashed border-[var(--ad-border)] opacity-60',
                  suruklenen === satir.key && 'ring-2 ring-[var(--ad-ring)]',
                )}
              >
                <span
                  aria-hidden
                  className="cursor-grab text-[var(--ad-text-muted)]"
                  title="Sürükleyip taşıyın"
                >
                  <Icon name="chevrons-up-down" size={16} />
                </span>

                {/* Klavye kullanıcısı için: sürükle-bırak tek başına
                    erişilebilir değil. */}
                <div className="flex flex-col">
                  <button
                    type="button"
                    aria-label={`${satir.label} yukarı taşı`}
                    disabled={index === 0}
                    onClick={() => tasi(index, -1)}
                    className="text-[var(--ad-text-muted)] disabled:opacity-30"
                  >
                    <Icon name="arrow-up" size={14} aria-hidden />
                  </button>
                  <button
                    type="button"
                    aria-label={`${satir.label} aşağı taşı`}
                    disabled={index === satirlar.length - 1}
                    onClick={() => tasi(index, 1)}
                    className="text-[var(--ad-text-muted)] disabled:opacity-30"
                  >
                    <Icon name="arrow-down" size={14} aria-hidden />
                  </button>
                </div>

                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={satir.visible}
                    onChange={(event) => guncelle(satir.key, { visible: event.target.checked })}
                    aria-label={`${satir.label} menüde görünsün`}
                  />
                  <Icon name={satir.icon as IconName} size={16} aria-hidden />
                </label>

                <Input
                  className="w-40"
                  value={satir.label}
                  aria-label={`${satir.varsayilan.label} etiketi`}
                  onChange={(event) => guncelle(satir.key, { label: event.target.value })}
                />

                <select
                  className="h-10 w-36 rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-2 text-xs"
                  value={satir.icon}
                  aria-label={`${satir.varsayilan.label} ikonu`}
                  onChange={(event) => guncelle(satir.key, { icon: event.target.value })}
                >
                  {IKON_ADLARI.map((ad) => (
                    <option key={ad} value={ad}>
                      {ad}
                    </option>
                  ))}
                </select>

                <Input
                  className="w-32"
                  list="menu-gruplari"
                  placeholder="Grup"
                  value={satir.group}
                  aria-label={`${satir.varsayilan.label} grubu`}
                  onChange={(event) => guncelle(satir.key, { group: event.target.value })}
                />

                {satir.label !== satir.varsayilan.label && (
                  <span className="text-xs text-[var(--ad-text-muted)]">
                    varsayılan: {satir.varsayilan.label}
                  </span>
                )}
              </div>
            ))}

            <datalist id="menu-gruplari">
              {gruplar.map((grup) => (
                <option key={grup} value={grup} />
              ))}
            </datalist>
          </CardBody>
        </Card>

        <Onizleme satirlar={satirlar} />
      </div>
    </>
  )
}

/**
 * Canlı önizleme.
 *
 * Yalnızca YAPILANDIRMAYI gösteriyor: gerçekte her kullanıcı ayrıca kendi
 * izinlerine ve derneğin planına göre süzülmüş bir menü görüyor. Bunu
 * söylememek, "gizlemediğim halde görünmüyor" sorusunu doğururdu.
 */
function Onizleme({ satirlar }: { satirlar: Satir[] }) {
  const gorunen = satirlar.filter((satir) => satir.visible)

  return (
    <Card className="h-fit">
      <CardHeader title="Önizleme" description="İzin ve paket süzgeci uygulanmadan." />
      <CardBody className="flex flex-col gap-0.5">
        {/* Gruplama SIRAYA göre: öğeleri gruba göre kümelemek sıralamayı
            bozardı ve sol menüdeki gerçek görünümle uyuşmazdı. */}
        {gorunen.map((satir, index) => (
          <Fragment key={satir.key}>
            {satir.group && satir.group !== gorunen[index - 1]?.group && (
              <p className="mt-2 px-2 text-[11px] font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                {satir.group}
              </p>
            )}
            <span className="flex items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-sm text-[var(--ad-text-muted)]">
              <Icon name={satir.icon as IconName} size={18} aria-hidden />
              {satir.label}
            </span>
          </Fragment>
        ))}
        {gorunen.length === 0 && (
          <p className="py-6 text-center text-xs text-[var(--ad-text-muted)]">
            Görünür öğe kalmadı.
          </p>
        )}
      </CardBody>
    </Card>
  )
}

function Sayfa({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Ayarlar</h1>
      <AyarlarSekmeleri />
      {children}
    </div>
  )
}
